import React, { useEffect, useState, useRef } from 'react';
import { useInView } from 'framer-motion';

/**
 * Animated counter that counts up when element comes into view
 * Supports currency formatting with € symbol and suffixes (M, k)
 */
const AnimatedCounter = ({ 
  value, 
  prefix = '', 
  suffix = '', 
  duration = 2000,
  className = '',
  immediate = false
}) => {
  const [displayValue, setDisplayValue] = useState(0);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });
  const isInView = immediate || inView;
  const hasAnimated = useRef(false);

  // Parse the target value from string like "2.0M", "250k", "100", "5"
  const parseValue = (val) => {
    if (typeof val === 'number') return val;
    const str = String(val).replace(/[€,+]/g, '');
    if (str.includes('M')) {
      return parseFloat(str.replace('M', '')) * 1000000;
    } else if (str.includes('k')) {
      return parseFloat(str.replace('k', '')) * 1000;
    }
    return parseFloat(str) || 0;
  };

  // Format the display value back to the original format
  const formatValue = (num, originalValue) => {
    const original = String(originalValue);
    if (original.includes('M')) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (original.includes('k')) {
      return Math.round(num / 1000) + 'k';
    }
    return Math.round(num).toString();
  };

  useEffect(() => {
    if (isInView && !hasAnimated.current) {
      hasAnimated.current = true;
      const targetValue = parseValue(value);
      const startTime = Date.now();
      
      const animate = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);
        
        // Easing function (ease-out cubic)
        const easeOut = 1 - Math.pow(1 - progress, 3);
        const currentValue = targetValue * easeOut;
        
        setDisplayValue(currentValue);
        
        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };
      
      requestAnimationFrame(animate);
    }
  }, [isInView, value, duration]);

  return (
    <span ref={ref} className={className}>
      {prefix}{formatValue(displayValue, value)}{suffix}
    </span>
  );
};

export default AnimatedCounter;
